'use client';

import React from 'react';
import { Question } from './types';
import { QuizItem } from '@/store/assessment-store';
import { CourseSection } from './components/CourseSection';
import { QuizProgressSection } from './components/QuizProgressSection';
import { QuestionsList } from './components/QuestionsList';

interface AssessmentSidebarProps {
  firstCourse?: {
    title: string;
    uid: string;
  };
  courseProgressPercentage: number;
  quizProgressPercentage: number;
  questions: Question[];
  questionIndex: number;
  quiz: QuizItem[];
  result?: any;
  quizData?: any; // Add quizData for correctness calculation
  onQuestionClick: (index: number) => void;
  type?: 'quiz' | 'assessment';
}

export function AssessmentSidebar({
  firstCourse,
  courseProgressPercentage,
  quizProgressPercentage,
  questions,
  questionIndex,
  quiz,
  result,
  quizData,
  onQuestionClick,
  type = 'quiz',
}: AssessmentSidebarProps) {
  // Function to determine if a question was answered correctly (only used in results mode for quiz; during quiz taking we show neutral icon)
  const handleGetQuestionCorrectness = React.useCallback((questionUid: string): boolean | null => {
    const question = questions.find((q) => q.metadata_uid === questionUid);
    if (!question) return null;

    // Results mode only: use API results (no fallback to CMS parsing)
    if (result) {
      const apiResults = result?.results;
      if (apiResults?.length) {
        const byUid = apiResults.find(
          (r: { questionUid?: string; question_uid?: string }) =>
            (r.questionUid ?? r.question_uid) === questionUid
        );
        if (byUid && typeof byUid.result === 'boolean') return byUid.result;
      }
      return null;
    }

    return null;
  }, [questions, result, type]);

  // Hide course section in results view
  const showCourseSection = firstCourse && !result;

  return (
    <div className="w-full">
      <div className="flex flex-col gap-6">
        {showCourseSection && (
          <CourseSection
            courseTitle={firstCourse.title}
            progressPercentage={courseProgressPercentage}
          />
        )}

        <QuizProgressSection progressPercentage={quizProgressPercentage} type={type}>
          <QuestionsList
            questions={questions}
            questionIndex={questionIndex}
            quiz={quiz}
            result={result}
            getQuestionCorrectness={handleGetQuestionCorrectness}
            onQuestionClick={onQuestionClick}
            type={type}
          />
        </QuizProgressSection>
      </div>
    </div>
  );
}

